import React from 'react';
import { useAppSelector } from '../../store';

import craftJsParser from '../../components/utils/craftJsParser';
import transferPropertiesToComponent from './transferPropertiesToComponent';
import { DataElementContext } from '../common/DataElementContext';
import { uniqueId } from 'lodash-es';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

export const defaultProps = {
  properties: {
    componentDataId: null,
    componentDataVersion: null,
    componentId: null,
    componentVersion: null,
  },
};

export const ComponentData = (componentProps) => {
  let props = componentProps;

  const components = useAppSelector((state) => state.templatesConfig.components);
  const componentsData = useAppSelector((state) => state.templatesConfig.componentsData);
  const componentsDataRef = useAppSelector((state) => state.templatesConfig.componentsDataRef);
  const setComponentsDefaults = useAppSelector((state) => state.templatesConfig.setComponentsDefaults);
  const setComponentsDataDefaults = useAppSelector((state) => state.templatesConfig.setComponentsDataDefaults);
  const authenticated = useAppSelector((state) => state.authentication && state.authentication.auth_type === 'user');

  const [__id] = React.useState(uniqueId('unique-'));
  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const { componentId: cId, componentDataId } = props.properties;
  const componentId = componentsDataRef[componentDataId] ?? cId;
  const rootProps = React.useRef({});

  let content = React.useMemo(() => {
    try {
      const projectData = components[componentId].projectData;
      const cdProperties = componentsData[componentDataId] != null ? componentsData[componentDataId] : {};

      const setComponentsDefaultsValue =
        setComponentsDefaults[componentId] != null ? setComponentsDefaults[componentId] : {};

      const visualIdentity =
        setComponentsDataDefaults[`${componentId}|${componentDataId}`] != null
          ? setComponentsDataDefaults[`${componentId}|${componentDataId}`]
          : {};

      const finalCDProperties = { ...cdProperties, ...visualIdentity };
      const data = JSON.parse(projectData);

      if (setComponentsDefaultsValue && Object.keys(setComponentsDefaultsValue).length)
        transferPropertiesToComponent(data, setComponentsDefaultsValue);
      if (finalCDProperties && Object.keys(finalCDProperties).length)
        transferPropertiesToComponent(data, finalCDProperties);

      // ensure that overrides from page layout and page are still applied because the above calls may have overwritten them
      if (props.plov && Object.keys(props.plov).length)
        transferPropertiesToComponent(data, props.plov);
      if (props.pov && Object.keys(props.pov).length)
        transferPropertiesToComponent(data, props.pov);

      let rootNodeId = 'ROOT';

      if (rootNodeId) {
        data[rootNodeId].type = 'Container';
        data[rootNodeId].props.styleText = 'width:100%;';

        /* dsId, deId, dsType, expose */
        const omittedKeys = { dsId: true, deId: true, dsType: true, expose: true };
        Object.keys(data?.[rootNodeId]?.props?.properties ?? {}).forEach((key) => {
          if (!omittedKeys[key]) {
            rootProps.current[key] = data[rootNodeId].props.properties[key];
          }
        });

        if (data?.[rootNodeId]?.nodes && data?.[rootNodeId]?.nodes.length === 1) {
          rootNodeId = data[rootNodeId].nodes[0];
        }

        if (!data?.[rootNodeId]?.props?.properties) {
          data[rootNodeId].props.properties = {};
        }

        if (window !== window.parent) {
          data[rootNodeId].props.properties['data-cjs-n-id'] = componentProps.id;
        }

        data[rootNodeId].props.properties['data-c-id'] = componentId;
        data[rootNodeId].props.properties['data-cd-id'] = componentDataId;

        return craftJsParser({
          craftState: data,
          rootNodeId: rootNodeId,
          pageId: componentDataId,
          pageLayoutId: componentId,
          pageType: 'componentsDataRef',
          options: {
            authenticated,
          },
          withProvider: true,
        });
      }
    } catch (err) {
      console.error('ComponentData', err);
    }
    return null;
  }, [
    components, componentsData, setComponentsDefaults, setComponentsDataDefaults,
    authenticated, componentId, componentDataId, componentProps.id, props.plov, props.pov
  ]);

  const contextValue = React.useMemo(() => {
    let contextValue = {
      ...rootProps.current,
      parentContext: dataElementContext,
      __id,
    };
    return contextValue;
  }, [dataElementContext, __id]);

  //console.log('ComponentData[contextValue]', contextValue);

  return <DataElementContext.Provider value={contextValue}>{content}</DataElementContext.Provider>;
};
